/**
 * UTM Parameter Utilities
 * Handles encoding, decoding, and retrieval of UTM parameters for tracking user acquisition
 */

import Cookies from 'js-cookie';
import moment from 'moment';
import { error } from '@/utils/logger';

// Constants for UTM storage
const UTM_COOKIE_NAME = 'gt_utm_params';
const UTM_TTL_DAYS = 14; // 14 days expiration

const MONGO_OBJECT_ID = /^[0-9a-fA-F]{24}$/;

/** Query keys preserved through share landings and typed post redirects. */
export const SHARE_ATTRIBUTION_QUERY_KEYS = [
  'creator_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'sr_type',
  'sr_id',
] as const;

export type ShareAttributionResourceType = 'post' | 'recommendation' | 'location' | 'profile';

/** Typed-route redirect keys copied alongside share attribution params. */
export const TYPED_POST_REDIRECT_QUERY_KEYS = [
  'token',
  'showShareCTA',
  ...SHARE_ATTRIBUTION_QUERY_KEYS,
] as const;

export function isValidMongoObjectId(value: string | undefined | null): value is string {
  return typeof value === 'string' && MONGO_OBJECT_ID.test(value);
}

/**
 * Appends creator share attribution query params (matches backend buildRedirectUrl).
 */
export function appendShareAttributionToSearchParams(
  params: URLSearchParams,
  options: {
    createdBy: string;
    resourceType: ShareAttributionResourceType;
    resourceId: string;
    /** Optional public handle for marketing ref= (additive; creator_id remains canonical). */
    ref?: string;
  },
): void {
  params.set('creator_id', options.createdBy);
  params.set('utm_source', 'creator');
  params.set('utm_medium', 'share');
  params.set('utm_campaign', 'creator_share');
  params.set('utm_content', options.createdBy);
  params.set('sr_type', options.resourceType);
  params.set('sr_id', options.resourceId);
  if (options.ref?.trim()) {
    params.set('ref', options.ref.trim());
  }
}

/** Copies share attribution + typed-route params from source onto target. */
export function copyShareAttributionSearchParams(
  target: URLSearchParams,
  source: URLSearchParams | null | undefined,
): void {
  if (!source) return;
  for (const key of TYPED_POST_REDIRECT_QUERY_KEYS) {
    const value = source.get(key);
    if (value) {
      target.set(key, value);
    }
  }
}

export interface UtmParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  /** Creator user id from share link query param (first-touch localStorage). */
  creator_id?: string;
  /** Share resource type for per-link signup attribution (backend signup_events). */
  sr_type?: string;
  /** Share resource id for per-link signup attribution. */
  sr_id?: string;
  referrer?: string;
  landing_page?: string;
  first_seen?: string;
}

/**
 * Encodes UTM parameters into a base64 string for OAuth state parameter
 * @param utms - UTM parameters object
 * @returns Base64 encoded string, or null if no UTMs
 */
export function encodeUtmsForState(utms: UtmParameters | null): string | null {
  if (!utms || Object.keys(utms).length === 0) {
    return null;
  }

  try {
    const json = JSON.stringify(utms);
    return btoa(json);
  } catch (err) {
    error('Failed to encode UTMs for state:', err);
    return null;
  }
}

/**
 * Decodes UTM parameters from OAuth state parameter
 * @param state - Base64 encoded state string
 * @returns Decoded UTM parameters, or null if invalid
 */
export function decodeUtmsFromState(state: string | null): UtmParameters | null {
  if (!state) {
    return null;
  }

  try {
    const json = atob(state);
    return JSON.parse(json);
  } catch (err) {
    error('Failed to decode UTMs from state:', err);
    return null;
  }
}

/**
 * Gets UTM parameters with fallback strategy
 * Priority: state parameter > cookies
 * @param stateParam - Optional state parameter from OAuth callback
 * @returns UTM parameters or null
 */
export function getUtmsWithFallback(stateParam?: string | null): UtmParameters | null {
  // Try state parameter first (most reliable for OAuth flows)
  if (stateParam) {
    const utmsFromState = decodeUtmsFromState(stateParam);
    if (utmsFromState) {
      return utmsFromState;
    }
  }

  // Fallback to cookies (for email/password flows or if state is missing)
  return getUtmsFromCookies();
}

/**
 * Extracts UTM parameters from the current URL and stores them with 14 days TTL
 * @returns UTM parameters object or null if no UTMs in URL
 */
export function getUtmsFromCurrentUrl(): UtmParameters | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);

  const fromUrl: UtmParameters = {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
    creator_id: params.get('creator_id') || undefined,
    sr_type: params.get('sr_type') || undefined,
    sr_id: params.get('sr_id') || undefined,
  };

  const filtered = Object.fromEntries(
    Object.entries(fromUrl).filter(([_unused, v]) => v !== undefined),
  ) as UtmParameters;

  if (Object.keys(filtered).length > 0) {
    const existing = getUtmsFromCookies() ?? {};
    const merged = { ...existing, ...filtered };
    saveUtmsToCookies(merged);
    return merged;
  }

  return getUtmsFromCookies();
}

/**
 * Saves UTM parameters to cookies with 14-day TTL
 * @param utms - UTM parameters to save
 */
function saveUtmsToCookies(utms: UtmParameters): void {
  try {
    // Use UTC timestamp for consistency
    const timestamp = moment.utc().toISOString();

    // Add first_seen timestamp if not already present
    const utmsWithTimestamp = {
      ...utms,
      first_seen: utms.first_seen || timestamp
    };

    const dataToStore = {
      utms: utmsWithTimestamp,
      timestamp,
    };

    // Save to cookie with automatic 14-day expiration
    Cookies.set(UTM_COOKIE_NAME, JSON.stringify(dataToStore), {
      expires: UTM_TTL_DAYS, // Cookie will automatically expire after 14 days
      sameSite: 'lax',
      secure: typeof window !== 'undefined' && window.location.protocol === 'https:'
    });
  } catch (e) {
    error('[UTM] Failed to save to cookie:', e);
  }
}

/**
 * Retrieves UTM parameters from cookies
 * @returns UTM parameters or null if not found
 */
function getUtmsFromCookies(): UtmParameters | null {
  try {
    const stored = Cookies.get(UTM_COOKIE_NAME);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    return parsed.utms;
  } catch (e) {
    error('[UTM] Error reading from cookie:', e);
    return null;
  }
}

/**
 * Prepares UTM parameters for backend API
 * Filters out undefined values and formats for API consumption
 * @param utms - Raw UTM parameters
 * @returns Object with utm_params key containing the cleaned UTM data
 */
export function prepareUtmsForBackend(utms: UtmParameters | null): { utm_params?: Record<string, string> } | null {
  if (!utms || Object.keys(utms).length === 0) {
    return null;
  }

  const cleaned: Record<string, string> = {};

  const utmContent =
    utms.utm_content ??
    (isValidMongoObjectId(utms.creator_id) ? utms.creator_id : undefined);

  // Only include defined UTM values
  if (utms.utm_source) cleaned.utm_source = utms.utm_source;
  if (utms.utm_medium) cleaned.utm_medium = utms.utm_medium;
  if (utms.utm_campaign) cleaned.utm_campaign = utms.utm_campaign;
  if (utmContent) cleaned.utm_content = utmContent;
  if (utms.utm_term) cleaned.utm_term = utms.utm_term;
  if (utms.sr_type) cleaned.sr_type = utms.sr_type;
  if (utms.sr_id) cleaned.sr_id = utms.sr_id;
  if (utms.referrer) cleaned.referrer = utms.referrer;
  if (utms.landing_page) cleaned.landing_page = utms.landing_page;
  if (utms.first_seen) cleaned.utm_timestamp = utms.first_seen;

  // Return nested object structure
  return Object.keys(cleaned).length > 0 ? { utm_params: cleaned } : null;
}

// ============================================================================
// OAUTH STATE (UTMs + optional post-auth redirect path)
// ============================================================================

/**
 * Shape of the extended OAuth state parameter that carries both UTM data and
 * an optional post-auth redirect destination through the Cognito round-trip.
 *
 * Backward-compatible: legacy state strings that contain only a flat UTM
 * object are decoded with `redirectPath: null`.
 */
export interface OAuthState {
  utm?: UtmParameters | null;
  redirectPath?: string | null;
  deferOnboarding?: boolean;
}

/**
 * Encodes UTMs and an optional redirect path into a base64 string suitable
 * for the OAuth `state` parameter.
 */
export function encodeOAuthState(payload: {
  utms?: UtmParameters | null;
  redirectPath?: string | null;
  /** Skip onboarding for this session and return straight to `redirectPath`. */
  deferOnboarding?: boolean;
}): string | null {
  const hasUtms = payload.utms && Object.keys(payload.utms).length > 0;
  const hasRedirect = !!payload.redirectPath;

  if (!hasUtms && !hasRedirect) return null;

  try {
    const obj: OAuthState = {};
    if (hasUtms) obj.utm = payload.utms;
    if (hasRedirect) obj.redirectPath = payload.redirectPath;
    if (hasRedirect && payload.deferOnboarding) obj.deferOnboarding = true;
    return btoa(JSON.stringify(obj));
  } catch (err) {
    error('Failed to encode OAuth state:', err);
    return null;
  }
}

/**
 * Decodes an OAuth `state` string produced by `encodeOAuthState`.
 *
 * Backward-compatible: if the state string contains a flat UTM object
 * (no `redirectPath` key) it is treated as UTM-only and
 * `redirectPath` is returned as `null`.
 */
export function decodeOAuthState(state: string | null): {
  utms: UtmParameters | null;
  redirectPath: string | null;
  deferOnboarding: boolean;
} {
  if (!state) return { utms: null, redirectPath: null, deferOnboarding: false };

  try {
    const parsed = JSON.parse(atob(state));

    // New format: object has `utm` or `redirectPath` keys
    if (parsed && (parsed.utm !== undefined || parsed.redirectPath !== undefined)) {
      return {
        utms: (parsed.utm as UtmParameters) ?? null,
        redirectPath: (parsed.redirectPath as string) ?? null,
        deferOnboarding: parsed.deferOnboarding === true,
      };
    }

    // Legacy format: flat UTM object
    return { utms: parsed as UtmParameters, redirectPath: null, deferOnboarding: false };
  } catch (err) {
    error('Failed to decode OAuth state:', err);
    return { utms: null, redirectPath: null, deferOnboarding: false };
  }
}

// ============================================================================
// ACQUISITION SOURCE TRACKING
// ============================================================================

/**
 * Acquisition source types for Mixpanel analytics
 */
export type AcquisitionSource = 'creator_driven' | 'paid' | 'organic' | 'direct';

/**
 * Source type for more granular tracking
 */
export type SourceType = 'creator_link' | 'paid' | 'organic' | 'direct';

// Known paid advertising sources
const PAID_SOURCES = ['google', 'facebook', 'instagram', 'tiktok', 'meta', 'twitter', 'linkedin', 'snapchat', 'pinterest'];
const PAID_MEDIUMS = ['cpc', 'paid', 'ppc', 'ads', 'display', 'banner', 'retargeting', 'paid_social'];

// Creator-related identifiers
const CREATOR_IDENTIFIERS = ['creator', 'share', 'shared', 'influencer', 'ambassador'];

/**
 * Derives the acquisition source from UTM parameters for Mixpanel charts.
 * Used to track: Creator-driven, Paid, Organic, or Direct traffic.
 *
 * @param utmParams - UTM parameters from the user's entry point
 * @returns Acquisition source category
 */
export function getAcquisitionSource(utmParams: UtmParameters | null): AcquisitionSource {
  if (!utmParams) {
    return 'direct';
  }

  if (utmParams.creator_id) {
    return 'creator_driven';
  }

  const source = utmParams.utm_source?.toLowerCase() || '';
  const medium = utmParams.utm_medium?.toLowerCase() || '';
  const campaign = utmParams.utm_campaign?.toLowerCase() || '';

  // Check for paid sources first (highest priority for attribution)
  const isPaidSource = PAID_SOURCES.some(ps => source.includes(ps));
  const isPaidMedium = PAID_MEDIUMS.some(pm => medium.includes(pm));
  if (isPaidSource && isPaidMedium) {
    return 'paid';
  }

  // Check for creator-driven traffic
  // Matches: utm_source=creator, utm_medium=share, utm_campaign contains 'creator', etc.
  const isCreatorSource = CREATOR_IDENTIFIERS.some(id => source.includes(id));
  const isCreatorMedium = CREATOR_IDENTIFIERS.some(id => medium.includes(id));
  const isCreatorCampaign = CREATOR_IDENTIFIERS.some(id => campaign.includes(id));
  if (isCreatorSource || isCreatorMedium || isCreatorCampaign) {
    return 'creator_driven';
  }

  // Has UTM params but not paid or creator = organic
  if (source || medium) {
    return 'organic';
  }

  return 'direct';
}

/** True when stored or URL attribution params indicate creator-driven first touch. */
export function hasCreatorShareAttribution(
  input: UtmParameters | Record<string, unknown> | null | undefined,
): boolean {
  if (!input || typeof input !== 'object') {
    return false;
  }
  const utmParams: UtmParameters = {
    utm_source: input.utm_source as string | undefined,
    utm_medium: input.utm_medium as string | undefined,
    utm_campaign: input.utm_campaign as string | undefined,
    utm_content: input.utm_content as string | undefined,
    utm_term: input.utm_term as string | undefined,
    creator_id: input.creator_id as string | undefined,
  };
  return getAcquisitionSource(utmParams) === 'creator_driven';
}

/**
 * Determines the source type for more granular conversion tracking.
 * Used for Anonymous → Signup conversion breakdown.
 *
 * @param utmParams - UTM parameters from the user's entry point
 * @returns Source type category
 */
export function getSourceType(utmParams: UtmParameters | null): SourceType {
  if (utmParams?.creator_id) {
    return 'creator_link';
  }

  if (!utmParams?.utm_source && !utmParams?.utm_medium) {
    return 'direct';
  }

  const source = utmParams.utm_source?.toLowerCase() || '';
  const medium = utmParams.utm_medium?.toLowerCase() || '';

  // Creator-shared links
  const isCreatorLink = CREATOR_IDENTIFIERS.some(id =>
    source.includes(id) || medium.includes(id)
  );
  if (isCreatorLink) {
    return 'creator_link';
  }

  // Paid campaigns
  const isPaidMedium = PAID_MEDIUMS.some(pm => medium.includes(pm));
  if (isPaidMedium) {
    return 'paid';
  }

  return 'organic';
}

/**
 * Enriches tracking data with acquisition source properties.
 * Use this to add source tracking to any Mixpanel event.
 * Note: Raw UTM params are auto-tracked by Mixpanel JS SDK, so we only add derived properties.
 *
 * @param utmParams - UTM parameters
 * @param additionalData - Additional event properties
 * @returns Enriched properties object
 */
export function enrichWithAcquisitionSource(
  utmParams: UtmParameters | null,
  additionalData?: Record<string, unknown>
): Record<string, unknown> {
  const acquisitionSource = getAcquisitionSource(utmParams);
  const sourceType = getSourceType(utmParams);

  return {
    ...additionalData,
    acquisition_source: acquisitionSource,
    source_type: sourceType
  };
}
